import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroDemoData'

/**
 * FABRICATED figures for the card Overview stats roll-up (EX-40632).
 *
 * Payment due and statement close are wired to real, contract-backed
 * endpoints — see `useCardOverviewStatsData`. What remains here:
 *
 * | field                  | blocked on                                      |
 * |------------------------|-------------------------------------------------|
 * | `budgetAllocated`      | EX-40688                                        |
 *
 * `budgetAllocated` is the one worth pausing on. It is not merely "the endpoint
 * isn't built": there is no statistic for a total budget limit anywhere in the
 * API, and the only assembled alternative is summing a *paginated* delegate-card
 * list, which is silently wrong past the first page. Do not "fix" this by
 * summing a page of budgets — a plausible wrong total is worse than an obviously
 * fake one. It needs a real aggregate from the backend.
 *
 * Read `useCardOverviewStatsData` before widening where this renders: these
 * figures reach a user the moment the surface is enabled for their org.
 *
 * Values match the design mockup so the demo reads coherently next to the real
 * limits data (balance $7,700 of a $50,000 limit).
 */
export type CardOverviewStatsDemo = {
  /** Total limit allocated across this card's budgets. */
  budgetAllocated: Money
  /**
   * Stand-in for the real limits endpoint, used only when it cannot be read —
   * see `useCardOverviewStatsData`. Utilization is DERIVED from these two rather
   * than stored, so the three figures can never disagree.
   */
  balance: Money
  limit: Money
}

export const CARD_OVERVIEW_STATS_DEMO: CardOverviewStatsDemo = {
  budgetAllocated: { amountMinor: 1000000, currency: 'USD' },
  // $7,700 of $50,000 → 15.4% utilization, matching the design mockup exactly.
  balance: { amountMinor: 770000, currency: 'USD' },
  limit: { amountMinor: 5000000, currency: 'USD' },
}
